import React, { useMemo, useState } from 'react';
import { Activity } from 'lucide-react';
import { StatusBadge } from '../../../components/deliveries/StatusBadge';
import { formatDateTime } from '../../../features/orderDetails/orderUtils';
import { shipmentTimelineOf } from '../../../features/tracking/trackingUtils';

/**
 * Everything that has happened to the parcel, oldest first.
 *
 * The shipment's own history and nothing of the order's: the operator's history
 * tab merges both, and here that put "En preparación" among the events of a
 * parcel the courier already had. Mapped by the same `toTimelineEvents` that tab
 * uses, which is what adds the `alt=media` Firebase needs before it hands over
 * a photo instead of its metadata.
 *
 * An empty history is not a contradiction of the badge above it: the backend
 * writes an event only when the status changes, so a shipment that never moved
 * has none — and every one of them, measured, is still "Pendiente".
 *
 * The dot takes the status' colour, so a parcel that passed through five states
 * looks like it passed through five states.
 *
 * @param {object} props
 * @param {object} props.order
 */
export const TrackingTimeline = ({ order }) => {
  const [previewImage, setPreviewImage] = useState(null);
  const timeline = useMemo(() => shipmentTimelineOf(order), [order]);

  return (
    <section
      data-testid="tracking-timeline"
      className="bg-white dark:bg-[#1a1a1a] rounded-2xl shadow-sm border border-border p-6 sm:p-8 mt-6"
    >
      <h2 className="text-lg font-bold text-ninja-dark dark:text-white mb-8 flex items-center gap-2">
        <Activity className="text-ninja-green" size={20} />
        Historial del pedido
      </h2>

      {timeline.length === 0 ? (
        <div className="py-10 flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-ninja-gray dark:bg-[#2d2d2d] rounded-full flex items-center justify-center mb-4">
            <Activity size={24} className="text-text-muted" />
          </div>
          <h3 className="text-base font-bold text-ninja-dark dark:text-white mb-2">
            Todavía sin movimientos
          </h3>
          <p className="text-text-secondary max-w-sm">
            Cuando el pedido empiece a moverse, vas a ver cada paso acá.
          </p>
        </div>
      ) : (
        <ol className="relative pl-6 border-l-2 border-border space-y-8">
          {timeline.map((event, index) => (
            <li
              // The two histories this merges number their rows separately, so
              // an id can repeat across the list.
              key={`${event.id}-${index}`}
              data-testid="tracking-timeline-event"
              data-status={event.status}
              className="relative"
            >
              <span
                className="absolute -left-[31px] top-1 w-4 h-4 rounded-full border-2 border-white dark:border-[#1a1a1a]"
                style={{ backgroundColor: event.color }}
              />

              <StatusBadge
                status={event.status}
                label={event.statusLabel}
                substatus={event.substatus}
                testId="tracking-timeline-status"
              />

              <p className="text-xs font-semibold text-text-muted mt-2">
                {formatDateTime(event.date)}
              </p>

              {event.details && (
                <p className="text-sm text-text-secondary mt-1">{event.details}</p>
              )}

              {event.imageUrls.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {event.imageUrls.map((url) => (
                    <button
                      key={url}
                      type="button"
                      onClick={() => setPreviewImage(url)}
                      data-testid="tracking-timeline-evidence"
                      className="w-14 h-14 rounded-lg overflow-hidden border border-border hover:border-ninja-green transition-colors"
                    >
                      <img
                        src={url}
                        alt="Evidencia de la entrega"
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ol>
      )}

      {previewImage && (
        <div
          role="presentation"
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setPreviewImage(null)}
        >
          <img
            src={previewImage}
            alt="Evidencia de la entrega"
            className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl"
          />
        </div>
      )}
    </section>
  );
};
